<script setup lang="ts">
import { ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/vue/24/outline';

const props = withDefaults(
  defineProps<{
    hasPrevious: boolean;
    hasNext: boolean;
    position: 'top' | 'bottom';
    loading?: boolean;
    // 'bar': bordered strip, right-aligned text buttons (optionally a rows
    // label on the left) — the table/desktop pager.
    // 'icon': chevron-only, no border — the compact pager next to the
    // status tabs.
    // 'split': two flex:1 text buttons, no border — the mobile card pager.
    variant?: 'bar' | 'icon' | 'split';
    rowsLabel?: string;
  }>(),
  { loading: false, variant: 'bar' }
);

const emit = defineEmits<{ previous: []; next: [] }>();

function testId(base: string): string {
  return props.position === 'top' ? `${base}-top` : base;
}
</script>

<template>
  <div v-if="variant === 'icon'" class="flex items-center gap-1.5">
    <span v-if="loading" :data-test="testId('queue-pagination-loading')">
      <ArrowPathIcon class="h-4 w-4 animate-spin text-gray-400" />
    </span>
    <button
      type="button"
      :data-test="testId('queue-previous-page')"
      aria-label="Previous page"
      class="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      :disabled="loading || !hasPrevious"
      @click="emit('previous')"
    >
      <ChevronLeftIcon class="h-4 w-4" />
    </button>
    <button
      type="button"
      :data-test="testId('queue-next-page')"
      aria-label="Next page"
      class="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
      :disabled="loading || !hasNext"
      @click="emit('next')"
    >
      <ChevronRightIcon class="h-4 w-4" />
    </button>
  </div>
  <div v-else-if="variant === 'split'" class="flex items-center gap-2 px-3 py-3">
    <button
      type="button"
      :data-test="testId('queue-previous-page')"
      class="flex flex-1 items-center justify-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="loading || !hasPrevious"
      @click="emit('previous')"
    >
      <ChevronLeftIcon class="h-4 w-4 flex-none" />
      Previous
    </button>
    <button
      type="button"
      :data-test="testId('queue-next-page')"
      class="flex flex-1 items-center justify-center gap-1 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
      :disabled="loading || !hasNext"
      @click="emit('next')"
    >
      Next
      <ChevronRightIcon class="h-4 w-4 flex-none" />
    </button>
  </div>
  <div
    v-else
    class="flex items-center gap-2 px-4 py-2"
    :class="[
      position === 'top' ? 'border-b border-gray-200' : 'border-t border-gray-200',
      rowsLabel ? 'justify-between' : 'justify-end',
    ]"
  >
    <span v-if="rowsLabel" class="text-sm text-gray-500">{{ rowsLabel }}</span>
    <div class="flex items-center gap-2">
      <span v-if="loading" :data-test="testId('queue-pagination-loading')">
        <ArrowPathIcon class="h-4 w-4 animate-spin text-gray-400" />
      </span>
      <button
        type="button"
        :data-test="testId('queue-previous-page')"
        class="flex items-center gap-1 rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="loading || !hasPrevious"
        @click="emit('previous')"
      >
        <ChevronLeftIcon class="h-4 w-4 flex-none" />
        Previous
      </button>
      <button
        type="button"
        :data-test="testId('queue-next-page')"
        class="flex items-center gap-1 rounded border border-gray-300 px-3 py-1 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="loading || !hasNext"
        @click="emit('next')"
      >
        Next
        <ChevronRightIcon class="h-4 w-4 flex-none" />
      </button>
    </div>
  </div>
</template>
